import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import api from '@/services/api';

type Step = 'org' | 'admin' | 'complete';

interface FormData {
  organizationName: string;
  slug: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  [key: string]: string;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { setTokens, setUser, setPermissions } = useAuthStore();
  const [step, setStep] = useState<Step>('org');
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [form, setForm] = useState<FormData>({
    organizationName: '',
    slug: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const updateField = (field: keyof FormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setServerError('');

    // Auto-generate slug from org name
    if (field === 'organizationName') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .substring(0, 50);
      setForm(prev => ({ ...prev, slug }));
    }
  };

  const validateOrgStep = (): boolean => {
    const errs: FormErrors = {};
    if (!form.organizationName.trim()) errs.organizationName = 'Organization name is required';
    else if (form.organizationName.length < 2) errs.organizationName = 'Must be at least 2 characters';
    if (!form.slug.trim()) errs.slug = 'Slug is required';
    else if (!/^[a-z0-9-]+$/.test(form.slug)) errs.slug = 'Only lowercase letters, numbers, and hyphens';
    else if (form.slug.length < 2) errs.slug = 'Must be at least 2 characters';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateAdminStep = (): boolean => {
    const errs: FormErrors = {};
    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    if (!form.password) errs.password = 'Password is required';
    else if (form.password.length < 8) errs.password = 'At least 8 characters';
    else if (!/[A-Z]/.test(form.password)) errs.password = 'Needs an uppercase letter';
    else if (!/[a-z]/.test(form.password)) errs.password = 'Needs a lowercase letter';
    else if (!/[0-9]/.test(form.password)) errs.password = 'Needs a number';
    else if (!/[^A-Za-z0-9]/.test(form.password)) errs.password = 'Needs a special character';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateAdminStep()) return;
    setIsLoading(true);
    setServerError('');

    try {
      const res = await api.post('/auth/register-organization', {
        organizationName: form.organizationName,
        slug: form.slug,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
      });

      const { accessToken, refreshToken, user, permissions } = res.data;
      setTokens(accessToken, refreshToken);
      setUser(user);
      if (permissions) setPermissions(permissions);
      setStep('complete');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Registration failed. Please try again.';
      setServerError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = (): { score: number; label: string; color: string } => {
    const p = form.password;
    if (!p) return { score: 0, label: '', color: '' };
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[a-z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score <= 2) return { score, label: 'Weak', color: '#ef4444' };
    if (score <= 3) return { score, label: 'Fair', color: '#eab308' };
    if (score <= 4) return { score, label: 'Good', color: '#3b82f6' };
    return { score, label: 'Strong', color: '#22c55e' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 mb-4 shadow-lg shadow-blue-500/25">
            <svg className="w-8 h-8 text-white" viewBox="0 0 32 32" fill="none">
              <path d="M8 22V14L16 10L24 14V22L16 26L8 22Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
              <path d="M16 18V10" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 14L16 18L24 14" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Create Your Organization</h1>
          <p className="text-slate-500 mt-1 text-sm">Set up your asset management platform</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-3 mb-8">
          {[
            { key: 'org', label: 'Organization' },
            { key: 'admin', label: 'Admin Account' },
            { key: 'complete', label: 'Done' },
          ].map((s, i) => {
            const steps: Step[] = ['org', 'admin', 'complete'];
            const currentIdx = steps.indexOf(step);
            const stepIdx = steps.indexOf(s.key as Step);
            const isActive = stepIdx === currentIdx;
            const isDone = stepIdx < currentIdx;
            return (
              <div key={s.key} className="flex items-center gap-3">
                {i > 0 && (
                  <div className={`w-8 h-0.5 ${isDone ? 'bg-blue-500' : 'bg-slate-200'}`} />
                )}
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    isDone ? 'bg-blue-500 text-white' : isActive ? 'bg-blue-500 text-white ring-4 ring-blue-100' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {isDone ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-blue-600' : isDone ? 'text-slate-700' : 'text-slate-400'}`}>
                    {s.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          {serverError && (
            <div className="mx-6 mt-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {serverError}
            </div>
          )}

          {/* Step 1: Organization */}
          {step === 'org' && (
            <div className="p-6 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Organization Details</h2>
                <p className="text-sm text-slate-500">Tell us about your organization</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Organization Name</label>
                  <input
                    type="text"
                    value={form.organizationName}
                    onChange={e => updateField('organizationName', e.target.value)}
                    placeholder="e.g. Acme Corporation"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.organizationName ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                  />
                  {errors.organizationName && <p className="text-red-500 text-xs mt-1">{errors.organizationName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">URL Slug</label>
                  <div className="flex items-center">
                    <span className="text-sm text-slate-400 mr-1">app/</span>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={e => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="acme-corp"
                      className={`flex-1 px-4 py-2.5 border rounded-lg text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.slug ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    />
                  </div>
                  {errors.slug && <p className="text-red-500 text-xs mt-1">{errors.slug}</p>}
                </div>
              </div>
              <button
                onClick={() => validateOrgStep() && setStep('admin')}
                className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all shadow-sm"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Admin Account */}
          {step === 'admin' && (
            <div className="p-6 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Admin Account</h2>
                <p className="text-sm text-slate-500">Create your administrator account for <strong>{form.organizationName}</strong></p>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">First Name</label>
                    <input
                      type="text"
                      value={form.firstName}
                      onChange={e => updateField('firstName', e.target.value)}
                      placeholder="John"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.firstName ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    />
                    {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Last Name</label>
                    <input
                      type="text"
                      value={form.lastName}
                      onChange={e => updateField('lastName', e.target.value)}
                      placeholder="Doe"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.lastName ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    />
                    {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => updateField('email', e.target.value)}
                    placeholder="admin@yourcompany.com"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.email ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => updateField('password', e.target.value)}
                    placeholder="Min 8 chars, upper, lower, number, special"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.password ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                  />
                  {form.password && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${(passwordStrength().score / 5) * 100}%`, backgroundColor: passwordStrength().color }}
                        />
                      </div>
                      <span className="text-xs font-medium" style={{ color: passwordStrength().color }}>{passwordStrength().label}</span>
                    </div>
                  )}
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
                  <input
                    type="password"
                    value={form.confirmPassword}
                    onChange={e => updateField('confirmPassword', e.target.value)}
                    placeholder="Re-enter password"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                  />
                  {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('org')}
                  className="px-6 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-all text-sm"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      Creating...
                    </span>
                  ) : 'Create Organization'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 'complete' && (
            <div className="p-8 text-center space-y-5">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">You're All Set!</h2>
                <p className="text-slate-500 text-sm mt-1">
                  <strong>{form.organizationName}</strong> has been created. You're now the Super Admin.
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-left text-sm space-y-2">
                <p className="text-slate-600"><strong>Next steps:</strong></p>
                <ul className="text-slate-500 space-y-1.5 ml-4 list-disc">
                  <li>Configure your branches &amp; departments</li>
                  <li>Set up asset categories &amp; brands</li>
                  <li>Invite team members</li>
                  <li>Start adding assets</li>
                </ul>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all shadow-sm"
              >
                Go to Dashboard →
              </button>
            </div>
          )}
        </div>

        {/* Footer link */}
        {step !== 'complete' && (
          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 font-medium hover:underline">Sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
}
